import { type CSSProperties, type FC, useEffect, useRef, useState } from 'react';

import { Play } from 'lucide-react';

import { useAfterLoad } from '@shared/lib';
import { getImageEntry } from '@shared/lib/responsive-images';
import { getVideoAspectRatio } from '@shared/lib/video-posters';
import { tokens } from '@shared/styles/tokens';
import { Picture } from '@shared/ui/Picture';

import styles from './VideoPlayer.module.css';

interface VideoPlayerProps {
  src?: string;
  title?: string;
}

const RUTUBE_ORIGIN = 'https://rutube.ru';

// Обложки скачивает yarn video:posters (scripts/video-posters.mjs) в public/video-posters
const posterOf = (src: string) => {
  const id = /rutube\.ru\/play\/embed\/([0-9a-f]{32})/.exec(src)?.[1];
  return id && `/video-posters/${id}.jpg`;
};

// Плеер Rutube читает autoplay из адреса — для нажатия раньше, чем плеер успел загрузиться
const withAutoplay = (src: string) => {
  const url = new URL(src);
  url.searchParams.set('autoplay', 'true');
  return url.href;
};

// Команды плеера Rutube — JSON-строкой через postMessage (тот же протокол, что у его событий)
const command = (frame: HTMLIFrameElement | null, type: string) =>
  frame?.contentWindow?.postMessage(JSON.stringify({ type, data: {} }), RUTUBE_ORIGIN);

/**
 * Обложка ролика с кнопкой запуска, плеер Rutube грузится за ней после загрузки страницы
 * (~1,3 МБ — не мешает первому экрану, Lighthouse 29.10.2026). Нажатие сразу включает видео
 * командой плееру: ждать загрузку после клика не нужно. Адреса, а не флаги: на другой карточке
 * компонент тот же, и состояние прежнего ролика к новому не переходит
 */
export const VideoPlayer: FC<VideoPlayerProps> = ({ src, title = 'Видео занятия' }) => {
  const afterLoad = useAfterLoad();
  const [startedSrc, setStartedSrc] = useState<string>();
  const [readySrc, setReadySrc] = useState<string>();
  const [autoplaySrc, setAutoplaySrc] = useState<string>();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const started = src !== undefined && startedSrc === src;
  const poster = src && posterOf(src);
  const posterSize = poster ? getImageEntry(poster) : undefined;
  const aspectRatio =
    getVideoAspectRatio(src) ?? (posterSize ? posterSize.width / posterSize.height : 16 / 9);
  const portrait = aspectRatio < 1;
  const posterStyle: CSSProperties & { '--poster-aspect-ratio': number } = {
    '--poster-aspect-ratio': aspectRatio,
  };

  // Плеер сообщает о готовности — после этого команда play срабатывает мгновенно
  useEffect(() => {
    if (!src) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== RUTUBE_ORIGIN || event.source !== frameRef.current?.contentWindow)
        return;
      if (typeof event.data !== 'string') return;
      try {
        const message: unknown = JSON.parse(event.data);
        if (
          typeof message === 'object' &&
          message !== null &&
          'type' in message &&
          message.type === 'player:ready'
        )
          setReadySrc(src);
      } catch {
        // Не JSON — чужое сообщение
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [src]);

  // Обложка исчезает вместе с фокусом — переносим его в плеер, клавиатура продолжает с того же места
  useEffect(() => {
    if (started) frameRef.current?.focus();
  }, [started]);

  if (!src) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.aspectBox}>
          <div className={styles.preview} data-static="true">
            <div className={styles.playContent}>
              <div className={styles.playIcon}>
                <Play aria-hidden />
              </div>
              <span>Видео появится позже</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const play = () => {
    setStartedSrc(src);
    if (readySrc === src) {
      // Плеер запускает видео по команде без звука — звук включаем той же командой нажатия
      command(frameRef.current, 'player:unMute');
      command(frameRef.current, 'player:play');
    } else {
      // Плеер ещё не загрузился (или не начинал) — грузим сразу с автозапуском
      setAutoplaySrc(src);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.aspectBox}>
        {(afterLoad || started) && (
          <iframe
            className={styles.iframe}
            // Новый адрес — новый iframe: смена src у вставленного добавила бы запись в историю,
            // и «Назад» листал бы плеер, а не уводил со страницы
            key={autoplaySrc === src ? 'autoplay' : 'preload'}
            ref={frameRef}
            src={autoplaySrc === src ? withAutoplay(src) : src}
            title={title}
            allow="autoplay; fullscreen"
            allowFullScreen
            // За обложкой плеер недоступен с клавиатуры и для скринридеров
            inert={!started}
          />
        )}
        {!started && (
          <button
            className={styles.preview}
            type="button"
            aria-label={`Смотреть видео: ${title}`}
            onClick={play}
          >
            {poster && portrait && (
              <Picture
                className={styles.posterBackdrop}
                sizes={`(max-width: ${tokens.breakpoints.tablet}) 100vw, 55vw`}
                src={poster}
                alt=""
              />
            )}
            {poster && (
              <span className={styles.posterFrame} data-portrait={portrait} style={posterStyle}>
                <Picture
                  className={styles.poster}
                  sizes={`(max-width: ${tokens.breakpoints.tablet}) 100vw, 55vw`}
                  src={poster}
                  alt=""
                />
              </span>
            )}
            <span className={styles.playButton}>
              <Play aria-hidden />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
